import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  Upload,
  X,
  Plus,
  Check,
  AlertTriangle,
  Loader2,
  GripVertical,
  Eye,
  CheckCircle2,
  AlertCircle,
  MinusCircle,
  ExternalLink,
  Building2,
  Users,
  FileUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { MoneyInput } from "@/components/MoneyInput";
import { parseMoney, type MoneyKind } from "@shared/money";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface OnePagerImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preloadedFile?: File | null;
}

interface ExtractedContact {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  bio?: string;
  isPrimary: boolean;
  include: boolean;
  confidence: {
    name: "high" | "medium" | "low" | null;
    title: "high" | "medium" | "low" | null;
    email: "high" | "medium" | "low" | null;
    phone: "high" | "medium" | "low" | null;
  };
}

interface ExtractedData {
  company: {
    name: string;
    firmType: string;
    website: string;
    description: string;
    confidence: {
      name: "high" | "medium" | "low" | null;
      firmType: "high" | "medium" | "low" | null;
      website: "high" | "medium" | "low" | null;
      description: "high" | "medium" | "low" | null;
    };
  };
  investmentCriteria: {
    targetRevenueLow: string;
    targetRevenueHigh: string;
    targetEbitdaLow: string;
    targetEbitdaHigh: string;
    geography: string;
    transactionTypes: string[];
    targetIndustries: string[];
    confidence: {
      revenue: "high" | "medium" | "low" | null;
      ebitda: "high" | "medium" | "low" | null;
      geography: "high" | "medium" | "low" | null;
    };
  };
  contacts: ExtractedContact[];
  additionalNotes: string;
}

interface DuplicateMatch {
  type: "company" | "contact";
  existingId: string;
  existingName: string;
  existingCompany?: string;
  addedAt?: string;
  contactCount?: number;
  lastActivity?: string;
}

type Step = "upload" | "processing" | "review" | "duplicates" | "success";

const firmTypes = [
  "Private Equity",
  "Family Office",
  "Strategic Buyer",
  "Independent Sponsor",
  "Search Fund",
  "Other",
];

const transactionTypeOptions = [
  "Majority Equity",
  "Minority Equity",
  "Platform",
  "Add-on",
  "Family/Founder-Owned",
  "Corporate Carveouts",
];

const emptyExtractedData: ExtractedData = {
  company: {
    name: "",
    firmType: "",
    website: "",
    description: "",
    confidence: { name: null, firmType: null, website: null, description: null },
  },
  investmentCriteria: {
    targetRevenueLow: "",
    targetRevenueHigh: "",
    targetEbitdaLow: "",
    targetEbitdaHigh: "",
    geography: "",
    transactionTypes: [],
    targetIndustries: [],
    confidence: { revenue: null, ebitda: null, geography: null },
  },
  contacts: [],
  additionalNotes: "",
};

export function OnePagerImportModal({ open, onOpenChange, preloadedFile }: OnePagerImportModalProps) {
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [extractedData, setExtractedData] = useState<ExtractedData>(emptyExtractedData);
  const [processingStep, setProcessingStep] = useState(0);
  const [attachDocument, setAttachDocument] = useState(true);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [duplicateAction, setDuplicateAction] = useState<Record<string, "skip" | "update" | "create">>({});
  const [createdCompanyName, setCreatedCompanyName] = useState("");
  const [createdContactCount, setCreatedContactCount] = useState(0);
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null);
  const [newIndustry, setNewIndustry] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleClose = () => {
    setStep("upload");
    setFiles([]);
    setExtractedData(emptyExtractedData);
    setProcessingStep(0);
    setDuplicates([]);
    setDuplicateAction({});
    onOpenChange(false);
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) =>
        f.type === "application/pdf" ||
        f.type === "image/png" ||
        f.type === "image/jpeg" ||
        f.type === "image/jpg"
    );
    if (droppedFiles.length > 0) {
      setFiles((prev) => [...prev, ...droppedFiles].slice(0, 3));
    } else if (e.dataTransfer.files.length > 0) {
      toast.error("Please upload PDF, PNG, or JPG files.");
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files).filter(
        (f) =>
          f.type === "application/pdf" ||
          f.type === "image/png" ||
          f.type === "image/jpeg" ||
          f.type === "image/jpg"
      );
      if (selectedFiles.length === 0 && e.target.files.length > 0) {
        toast.error("Please upload PDF, PNG, or JPG files.");
      }
      setFiles((prev) => [...prev, ...selectedFiles].slice(0, 3));
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const extractMutation = useMutation({
    mutationFn: async (filesToProcess: File[]) => {
      const formData = new FormData();
      filesToProcess.forEach((file, i) => {
        formData.append(`file${i}`, file);
      });

      const res = await fetch("/api/extract-one-pager", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Extraction failed");
      }

      return res.json();
    },
    onSuccess: (data) => {
      setExtractedData(data);
      setStep("review");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to extract document");
      setStep("upload");
    },
  });

  // "$5M - $25M" from whatever was typed ("5", "5M", "5,000,000"); null
  // unless both ends are clean amounts. Was `$${low}M`, which turned a typed
  // "500K" into "$500KM".
  const rangeLabel = (lo: string, hi: string, kind: MoneyKind) => {
    const a = parseMoney(lo, kind);
    const b = parseMoney(hi, kind);
    return a.status === "ok" && b.status === "ok" ? `${a.display} - ${b.display}` : null;
  };

  const createBuyerMutation = useMutation({
    mutationFn: async (data: ExtractedData) => {
      const payload = {
        company: {
          name: data.company.name,
          category: "Buyer Network",
          subType: data.company.firmType,
          website: data.company.website,
          notes: data.company.description,
          targetRevenue: rangeLabel(data.investmentCriteria.targetRevenueLow, data.investmentCriteria.targetRevenueHigh, "revenue"),
          ebitdaRange: rangeLabel(data.investmentCriteria.targetEbitdaLow, data.investmentCriteria.targetEbitdaHigh, "buyerEbitda"),
          targetIndustries: data.investmentCriteria.targetIndustries,
          specialty: data.investmentCriteria.geography,
        },
        contacts: data.contacts
          .filter((c) => c.include)
          .map((c) => ({
            name: c.name,
            title: c.title,
            email: c.email,
            phone: c.phone,
            isPrimary: c.isPrimary,
            notes: c.bio || null,
          })),
        additionalNotes: data.additionalNotes,
        investmentCriteria: data.investmentCriteria,
        attachDocument,
        duplicateActions: duplicateAction,
      };

      const res = await fetch("/api/import-buyer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create buyer");
      }

      return res.json();
    },
    onSuccess: (data) => {
      setCreatedCompanyName(data.company.name);
      setCreatedContactCount(data.contactsCreated || 0);
      setCreatedCompanyId(data.company.id);
      queryClient.invalidateQueries({ queryKey: ["buyers"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      setStep("success");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create buyer");
    },
  });

  const handleProcessDocument = async () => {
    if (files.length === 0) return;
    setStep("processing");
    setProcessingStep(0);

    const steps = ["reading", "extracting_company", "extracting_contacts", "extracting_criteria"];
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setProcessingStep(currentStep);
      }
    }, 1500);

    try {
      await extractMutation.mutateAsync(files);
    } finally {
      clearInterval(interval);
    }
  };

  const handleCreateBuyer = () => {
    createBuyerMutation.mutate(extractedData);
  };

  const updateCompanyField = (field: keyof ExtractedData["company"], value: string) => {
    setExtractedData((prev) => ({
      ...prev,
      company: { ...prev.company, [field]: value },
    }));
  };

  const updateCriteriaField = (field: keyof ExtractedData["investmentCriteria"], value: any) => {
    setExtractedData((prev) => ({
      ...prev,
      investmentCriteria: { ...prev.investmentCriteria, [field]: value },
    }));
  };

  const updateContact = (id: string, updates: Partial<ExtractedContact>) => {
    setExtractedData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
  };

  const addContact = () => {
    const newContact: ExtractedContact = {
      id: `new-${Date.now()}`,
      name: "",
      title: "",
      email: "",
      phone: "",
      isPrimary: false,
      include: true,
      confidence: { name: null, title: null, email: null, phone: null },
    };
    setExtractedData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, newContact],
    }));
  };

  const toggleTransactionType = (type: string) => {
    setExtractedData((prev) => ({
      ...prev,
      investmentCriteria: {
        ...prev.investmentCriteria,
        transactionTypes: prev.investmentCriteria.transactionTypes.includes(type)
          ? prev.investmentCriteria.transactionTypes.filter((t) => t !== type)
          : [...prev.investmentCriteria.transactionTypes, type],
      },
    }));
  };

  const removeIndustry = (industry: string) => {
    setExtractedData((prev) => ({
      ...prev,
      investmentCriteria: {
        ...prev.investmentCriteria,
        targetIndustries: prev.investmentCriteria.targetIndustries.filter((i) => i !== industry),
      },
    }));
  };

  const addIndustry = () => {
    if (newIndustry.trim()) {
      setExtractedData((prev) => ({
        ...prev,
        investmentCriteria: {
          ...prev.investmentCriteria,
          targetIndustries: [...prev.investmentCriteria.targetIndustries, newIndustry.trim()],
        },
      }));
      setNewIndustry("");
    }
  };

  const ConfidenceIcon = ({ level }: { level: "high" | "medium" | "low" | null }) => {
    if (level === "high") return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (level === "medium") return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    if (level === "low") return <MinusCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  const processingSteps = [
    { label: "Reading pages", done: processingStep > 0 },
    { label: "Extracting company info", done: processingStep > 1 },
    { label: "Extracting contacts", done: processingStep > 2 },
    { label: "Extracting investment criteria", done: processingStep > 3 },
  ];

  const contactsToImport = extractedData.contacts.filter((c) => c.include);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className={cn(
            "gap-0 p-0 overflow-hidden",
            step === "review" ? "max-w-3xl max-h-[90vh]" : "max-w-lg"
          )}
          data-testid="one-pager-import-modal"
        >
          <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 to-secondary/5">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-text" />
              {step === "upload" && "Import from One-Pager"}
              {step === "processing" && "Analyzing Document..."}
              {step === "review" && "Review Extracted Information"}
              {step === "duplicates" && "Duplicate Detection"}
              {step === "success" && "Import Successful!"}
            </DialogTitle>
          </DialogHeader>

          {step === "upload" && (
            <div className="p-6 space-y-4">
              <div
                className={cn(
                  "border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer",
                  files.length > 0
                    ? "border-primary/50 bg-primary/5"
                    : "border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50"
                )}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="font-medium mb-1">Drag & drop files here</p>
                <p className="text-sm text-muted-foreground">
                  or click to browse. Accepts PDF, PNG, JPG (max 32MB for PDFs)
                </p>
              </div>

              {files.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Uploaded files:</p>
                  {files.map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                    >
                      <FileText className="w-5 h-5 text-primary-text" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(i);
                        }}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {files.length < 3 && files[0]?.type !== "application/pdf" && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-2 border border-dashed rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary-text flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Page
                    </button>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleProcessDocument}
                  disabled={files.length === 0}
                  data-testid="process-document-btn"
                >
                  Process Document
                </Button>
              </div>
            </div>
          )}

          {step === "processing" && (
            <div className="p-8 space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-text animate-spin" />
                </div>
              </div>
              <p className="text-center text-lg font-medium">Analyzing document...</p>
              <div className="space-y-3">
                {processingSteps.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center",
                        s.done
                          ? "bg-green-500 text-white"
                          : i === processingStep
                          ? "bg-primary text-white"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {s.done ? (
                        <Check className="w-4 h-4" />
                      ) : i === processingStep ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <span className="text-xs">{i + 1}</span>
                      )}
                    </div>
                    <span
                      className={cn(
                        "text-sm",
                        s.done || i === processingStep ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === "review" && (
            <ScrollArea className="flex-1 max-h-[65vh]">
              <div className="p-6 space-y-6">
                <p className="text-sm text-muted-foreground">
                  We found the following information. Review and edit before saving.
                </p>

                <div className="space-y-4 p-4 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-text" />
                    <h3 className="font-semibold">Company Information</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        Company Name
                        <ConfidenceIcon level={extractedData.company.confidence.name} />
                      </Label>
                      <Input
                        value={extractedData.company.name}
                        onChange={(e) => updateCompanyField("name", e.target.value)}
                        className="mt-1"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        Firm Type
                        <ConfidenceIcon level={extractedData.company.confidence.firmType} />
                      </Label>
                      <Select
                        value={extractedData.company.firmType}
                        onValueChange={(v) => updateCompanyField("firmType", v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select firm type" />
                        </SelectTrigger>
                        <SelectContent>
                          {firmTypes.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        Website
                        <ConfidenceIcon level={extractedData.company.confidence.website} />
                      </Label>
                      <Input
                        value={extractedData.company.website}
                        onChange={(e) => updateCompanyField("website", e.target.value)}
                        className="mt-1"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="flex items-center gap-2">
                        Description
                        <ConfidenceIcon level={extractedData.company.confidence.description} />
                      </Label>
                      <Textarea
                        value={extractedData.company.description}
                        onChange={(e) => updateCompanyField("description", e.target.value)}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 rounded-lg border">
                  <h3 className="font-semibold">Investment Criteria</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        Target Revenue Range
                        <ConfidenceIcon level={extractedData.investmentCriteria.confidence.revenue} />
                      </Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <MoneyInput
                          kind="revenue"
                          aria-label="Target revenue low"
                          value={extractedData.investmentCriteria.targetRevenueLow}
                          onValueChange={(v) => updateCriteriaField("targetRevenueLow", v)}
                          placeholder="Low, e.g. 5M"
                        />
                        <MoneyInput
                          kind="revenue"
                          aria-label="Target revenue high"
                          value={extractedData.investmentCriteria.targetRevenueHigh}
                          onValueChange={(v) => updateCriteriaField("targetRevenueHigh", v)}
                          placeholder="High"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        Target EBITDA Range
                        <ConfidenceIcon level={extractedData.investmentCriteria.confidence.ebitda} />
                      </Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <MoneyInput
                          kind="buyerEbitda"
                          aria-label="Target EBITDA low"
                          value={extractedData.investmentCriteria.targetEbitdaLow}
                          onValueChange={(v) => updateCriteriaField("targetEbitdaLow", v)}
                          placeholder="Low, e.g. 5M"
                        />
                        <MoneyInput
                          kind="buyerEbitda"
                          aria-label="Target EBITDA high"
                          value={extractedData.investmentCriteria.targetEbitdaHigh}
                          onValueChange={(v) => updateCriteriaField("targetEbitdaHigh", v)}
                          placeholder="High"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label className="flex items-center gap-2">
                        Geography
                        <ConfidenceIcon level={extractedData.investmentCriteria.confidence.geography} />
                      </Label>
                      <Input
                        value={extractedData.investmentCriteria.geography}
                        onChange={(e) => updateCriteriaField("geography", e.target.value)}
                        className="mt-1"
                        placeholder="e.g., Southwest US, Texas"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Transaction Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {transactionTypeOptions.map((t) => (
                        <label
                          key={t}
                          className={cn(
                            "flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-all text-sm",
                            extractedData.investmentCriteria.transactionTypes.includes(t)
                              ? "border-primary bg-primary/10"
                              : "border-muted hover:border-primary/50"
                          )}
                        >
                          <Checkbox
                            checked={extractedData.investmentCriteria.transactionTypes.includes(t)}
                            onCheckedChange={() => toggleTransactionType(t)}
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Target Industries</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {extractedData.investmentCriteria.targetIndustries.map((ind) => (
                        <Badge key={ind} variant="secondary" className="flex items-center gap-1">
                          {ind}
                          <button onClick={() => removeIndustry(ind)}>
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      <div className="flex items-center gap-1">
                        <Input
                          value={newIndustry}
                          onChange={(e) => setNewIndustry(e.target.value)}
                          placeholder="Add industry"
                          className="h-7 w-32 text-sm"
                          onKeyDown={(e) => e.key === "Enter" && addIndustry()}
                        />
                        <Button size="sm" variant="ghost" onClick={addIndustry} className="h-7 px-2">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary-text" />
                      <h3 className="font-semibold">Contacts Found ({extractedData.contacts.length})</h3>
                    </div>
                  </div>

                  {extractedData.contacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No contacts found in document.</p>
                  ) : (
                    <div className="space-y-3">
                      {extractedData.contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className={cn(
                            "p-3 rounded-lg border",
                            contact.include ? "bg-muted/30" : "bg-muted/10 opacity-60"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={contact.include}
                              onCheckedChange={(checked) =>
                                updateContact(contact.id, { include: checked as boolean })
                              }
                            />
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs flex items-center gap-1">
                                  Name
                                  <ConfidenceIcon level={contact.confidence.name} />
                                </Label>
                                <Input
                                  value={contact.name}
                                  onChange={(e) => updateContact(contact.id, { name: e.target.value })}
                                  className="h-8 text-sm mt-0.5"
                                />
                              </div>
                              <div>
                                <Label className="text-xs flex items-center gap-1">
                                  Title
                                  <ConfidenceIcon level={contact.confidence.title} />
                                </Label>
                                <Input
                                  value={contact.title}
                                  onChange={(e) => updateContact(contact.id, { title: e.target.value })}
                                  className="h-8 text-sm mt-0.5"
                                />
                              </div>
                              <div>
                                <Label className="text-xs flex items-center gap-1">
                                  Email
                                  <ConfidenceIcon level={contact.confidence.email} />
                                </Label>
                                <Input
                                  value={contact.email}
                                  onChange={(e) => updateContact(contact.id, { email: e.target.value })}
                                  className="h-8 text-sm mt-0.5"
                                />
                              </div>
                              <div>
                                <Label className="text-xs flex items-center gap-1">
                                  Phone
                                  <ConfidenceIcon level={contact.confidence.phone} />
                                </Label>
                                <Input
                                  value={contact.phone}
                                  onChange={(e) => updateContact(contact.id, { phone: e.target.value })}
                                  className="h-8 text-sm mt-0.5"
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1 text-xs cursor-pointer">
                                <RadioGroupItem
                                  value={contact.id}
                                  checked={contact.isPrimary}
                                  onClick={() => {
                                    setExtractedData((prev) => ({
                                      ...prev,
                                      contacts: prev.contacts.map((c) => ({
                                        ...c,
                                        isPrimary: c.id === contact.id,
                                      })),
                                    }));
                                  }}
                                />
                                Primary
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={addContact}
                    className="w-full p-2 border border-dashed rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary-text flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Contact Manually
                  </button>
                </div>

                <div className="space-y-3 p-4 rounded-lg border">
                  <h3 className="font-semibold">Additional Notes</h3>
                  <Textarea
                    value={extractedData.additionalNotes}
                    onChange={(e) =>
                      setExtractedData((prev) => ({ ...prev, additionalNotes: e.target.value }))
                    }
                    rows={4}
                    placeholder="Prior investments, fund size, AUM, team background..."
                  />
                </div>

                <div className="space-y-3 p-4 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <FileUp className="w-5 h-5 text-primary-text" />
                    <h3 className="font-semibold">Source Document</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded border bg-muted flex items-center justify-center">
                      <FileText className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{files[0]?.name || "Document"}</p>
                      <p className="text-xs text-muted-foreground">
                        Uploaded {new Date().toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={attachDocument}
                      onCheckedChange={(checked) => setAttachDocument(checked as boolean)}
                    />
                    <span className="text-sm">Attach document to company record</span>
                  </label>
                </div>
              </div>
            </ScrollArea>
          )}

          {step === "success" && (
            <div className="p-8 text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Successfully Imported!</h3>
                <p className="text-muted-foreground">
                  Created <strong>{createdCompanyName}</strong>
                  {createdContactCount > 0 && ` with ${createdContactCount} contact${createdContactCount > 1 ? "s" : ""}`}
                </p>
              </div>
              <div className="flex justify-center gap-3">
                {createdCompanyId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleClose();
                      window.location.href = `/buyers/${createdCompanyId}`;
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Buyer
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep("upload");
                    setFiles([]);
                    setExtractedData(emptyExtractedData);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Import Another
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </div>
            </div>
          )}

          {step === "review" && (
            <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateBuyer}
                disabled={!extractedData.company.name || createBuyerMutation.isPending}
                data-testid="create-buyer-btn"
              >
                {createBuyerMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {contactsToImport.length > 0
                  ? `Create Buyer and ${contactsToImport.length} Contact${contactsToImport.length > 1 ? "s" : ""}`
                  : "Create Buyer"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
